import React, { useMemo, useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import {
  LineChart,
  ResponsiveContainer,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { getLoginDataFromStorage } from "@shared/utils/loginUtils";
import { logEvent } from "@shared/analytics/analytics";
import { useService, useUsageChart } from "../../hook";
import { useTranslation } from "react-i18next";

export const ConsumptionWidget = () => {
  const { t } = useTranslation();
  const { remoteUtilityId, remoteConsumerNumber, consumerId } = getLoginDataFromStorage();

  const { data: serviceData } = useService({
    consumer: consumerId,
  });

  const activeServices = useMemo(
    () => serviceData?.result || [],
    [serviceData]
  );

  const [utilityFilter, setUtilityFilter] = useState("");

  useEffect(() => {
    logEvent("Utility filter opened");
    if (activeServices.length > 0 && !utilityFilter) {
      setUtilityFilter(activeServices[0]);
    }
  }, [activeServices, utilityFilter]);

  const { data: graphData } = useUsageChart({
    consumer_no: remoteConsumerNumber,
    remote_utility_id: remoteUtilityId,
    fetch_last_six_records: "True",
    utility_service: utilityFilter
  });

  const transformedUsageData = useMemo(() => {
    if (!graphData?.result) return [];
    
    return Object.entries(graphData.result).map(([month, data]: [string, any]) => ({
      name: month,
      consumption: data.consumption,
      [utilityFilter.toLowerCase()]: data.consumption,
    }));
  }, [graphData, utilityFilter]);

  const handleUtilityChange = (value: string) => {
    setUtilityFilter(value);
  };

  return (
    <Card className="rounded-2xl shadow-sm border-slate-200">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="mb-4">
            <CardTitle className="text-lg font-bold text-slate-800">{t("Usage Trends")}</CardTitle>
            <CardDescription>
              {t("Monitor your consumption over time")}
            </CardDescription>
          </div>
          <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
            <Select value={utilityFilter} onValueChange={handleUtilityChange}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder={t("Select utility")} />
              </SelectTrigger>
              <SelectContent>
                {activeServices.map((service: string, index: number) => (
                  <SelectItem key={index} value={service}>
                    {service}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-2">
        <div className="h-[300px] flex items-center justify-center">
          {transformedUsageData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={transformedUsageData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="consumption"
                  stroke="#0099cc"
                  activeDot={{ r: 8 }}
                  name={`${utilityFilter} ${t("Consumption")}`}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-muted-foreground">
              {utilityFilter ? t("Loading usage data...") : t("Select a utility service to view data")}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
